/**
 * @file Section navigation for the Mapping Review panel. A whole-document
 * review shows the real Form values / Tables `Segmented` switcher (both tabs
 * live — the Form values surface stays useful even with no header fields, via
 * its add-field / add-reference affordances). An entity-records import has a
 * single surface, so it shows a non-interactive mode badge instead of a faked
 * two-option switcher with one tab permanently disabled. For the import it
 * also renders the shared per-table source selector (`MappingReviewChunkNav`)
 * and the clickable Typesafe tag legend whose counts double as a row filter
 * (labelled "Filter rows:", with an active-filter breadcrumb + clear control),
 * both bound to the import slice and rendered through `mappingReviewLabels`.
 * Whole-document tables carry their own per-table source nav inside
 * `MappingReviewTables`, so the section-level nav is import-only. The active
 * section is owned by `MappingReviewPanel`; the active chunk lives on the slice
 * proxy (`activeChunkId`).
 */
import { FC } from 'react';
import { Button, Segmented, Tag, Typography, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import type { RowClassificationTag } from '#types';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import type { MappingReviewSession, MappingReviewTableSlice } from './mappingReviewState';
import { mappingReviewLabels } from './mappingReviewLabels';
import MappingReviewChunkNav from './MappingReviewChunkNav';

const { Text } = Typography;

export type ReviewSection = 'fields' | 'tables';

const ImportTagLegend: FC<{
  slice: MappingReviewTableSlice;
  tagFilter: RowClassificationTag | null;
  onTagFilterChange: (tag: RowClassificationTag | null) => void;
}> = ({ slice, tagFilter, onTagFilterChange }) => {
  const { token } = theme.useToken();
  const snap = useSnapshot(slice);
  const total = snap.baseRows.length;
  const tagCounts = snap.baseRows.reduce<Record<RowClassificationTag, number>>(
    (acc, r) => ({ ...acc, [r.tag]: acc[r.tag] + 1 }),
    { value: 0, summary: 0, junk: 0 },
  );
  return (
    <FlexCol noGrow gap={5}>
      <FlexBox noGrow gap={5} wrap alignStart>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Filter rows:
        </Text>
        {(Object.keys(mappingReviewLabels.tagColor) as RowClassificationTag[]).map((tag) => (
          <Tag.CheckableTag
            key={tag}
            checked={tagFilter === tag}
            onChange={(checked) => onTagFilterChange(checked ? tag : null)}
            aria-label={`Show only ${mappingReviewLabels.rowClass[tag]} rows`}
            aria-pressed={tagFilter === tag}
          >
            <Tag color={mappingReviewLabels.tagColor[tag]} style={{ margin: 0 }}>
              {tagCounts[tag]} {mappingReviewLabels.rowClass[tag]}
            </Tag>
          </Tag.CheckableTag>
        ))}
        {tagFilter && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label="Clear row filter"
            onClick={() => onTagFilterChange(null)}
          >
            Clear
          </Button>
        )}
      </FlexBox>
      {tagFilter && (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          showing only {mappingReviewLabels.rowClass[tagFilter]} ({tagCounts[tagFilter]} of {total})
        </Text>
      )}
    </FlexCol>
  );
};

interface MappingReviewSectionsProps {
  session: MappingReviewSession;
  section: ReviewSection;
  onSectionChange: (section: ReviewSection) => void;
  tagFilter: RowClassificationTag | null;
  onTagFilterChange: (tag: RowClassificationTag | null) => void;
}

const MappingReviewSections: FC<MappingReviewSectionsProps> = ({
  session,
  section,
  onSectionChange,
  tagFilter,
  onTagFilterChange,
}) => {
  const snap = useSnapshot(session);
  const isDocument = snap.target?.kind === 'document';
  const importSlice = snap.import ? session.import : null;

  return (
    <FlexCol noGrow gap={5}>
      {isDocument ? (
        <Segmented
          size="small"
          aria-label="Select review section"
          value={section}
          options={[
            { value: 'fields', label: 'Form values' },
            { value: 'tables', label: 'Tables' },
          ]}
          onChange={(value) => onSectionChange(value as ReviewSection)}
        />
      ) : (
        <Tag style={{ margin: 0, alignSelf: 'flex-start' }}>Table import</Tag>
      )}
      {importSlice && (
        <FlexBox noGrow stretch wrap alignStart gap={10}>
          <MappingReviewChunkNav slice={importSlice} />
          <ImportTagLegend
            slice={importSlice}
            tagFilter={tagFilter}
            onTagFilterChange={onTagFilterChange}
          />
        </FlexBox>
      )}
    </FlexCol>
  );
};

export default MappingReviewSections;
